/**
 * Audit Logs Page
 * Track and monitor system activity
 */

import React, { useEffect, useState } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import { auditService } from "../services/api/audit.service";
import type { AuditLog } from "../types/audit.types";
import BusyOverlay from "../components/common/BusyOverlay";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import "../styles/pages/AuditLogs.css";

// Get date one week ago
const getOneWeekAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return date.toISOString().split("T")[0];
};

// Get today's date
const getToday = () => {
  return new Date().toISOString().split("T")[0];
};

export const AuditLogs: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    search: "",
    action: "",
    entity: "",
    startDate: getOneWeekAgo(),
    endDate: getToday(),
  });

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Audit Logs", "Track and monitor system activity");
    fetchAuditLogs();
  }, [setPageTitle]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await auditService.getAuditLogs();
      console.log("Audit logs fetched:", data);
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching audit logs:", err);
      console.error("Error response data:", err?.response?.data);
      const errorMessage =
        err?.response?.data?.title ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load audit logs";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getActionBadgeClass = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes("create") || lowerAction.includes("insert"))
      return "action-badge--create";
    if (lowerAction.includes("update") || lowerAction.includes("modify"))
      return "action-badge--update";
    if (lowerAction.includes("delete") || lowerAction.includes("remove"))
      return "action-badge--delete";
    if (lowerAction.includes("login") || lowerAction.includes("logout"))
      return "action-badge--login";
    return "";
  };

  const filteredLogs = auditLogs.filter((log) => {
    const matchesSearch =
      log.fullName.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.userName.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.entityName.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.userAction.toLowerCase().includes(filters.search.toLowerCase());

    const matchesAction =
      !filters.action ||
      log.userAction.toLowerCase().includes(filters.action.toLowerCase());

    const matchesEntity =
      !filters.entity ||
      log.entityName.toLowerCase().includes(filters.entity.toLowerCase());

    // Date range filter
    const logDate = new Date(log.createdAt).toISOString().split("T")[0];
    const matchesDateRange =
      (!filters.startDate || logDate >= filters.startDate) &&
      (!filters.endDate || logDate <= filters.endDate);

    return matchesSearch && matchesAction && matchesEntity && matchesDateRange;
  });

  // Get unique actions and entities for filters
  const uniqueActions = [
    ...new Set(auditLogs.map((log) => log.userAction)),
  ].sort();
  const uniqueEntities = [
    ...new Set(auditLogs.map((log) => log.entityName)),
  ].sort();

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="audit-logs-page">
          <BusyOverlay isVisible={loading} message="Loading audit logs..." />

          <div className="audit-logs-content">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Filters */}
            <div className="audit-logs-filters">
              <div className="filter-group">
                <label className="filter-label">Search</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by user, entity, or action..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Action</label>
                <select
                  className="filter-select"
                  value={filters.action}
                  onChange={(e) =>
                    setFilters({ ...filters, action: e.target.value })
                  }
                >
                  <option value="">All Actions</option>
                  {uniqueActions.map((action) => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Entity</label>
                <select
                  className="filter-select"
                  value={filters.entity}
                  onChange={(e) =>
                    setFilters({ ...filters, entity: e.target.value })
                  }
                >
                  <option value="">All Entities</option>
                  {uniqueEntities.map((entity) => (
                    <option key={entity} value={entity}>
                      {entity}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Start Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">End Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Table */}
            {filteredLogs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state__icon">📋</div>
                <p className="empty-state__text">
                  {filters.search ||
                  filters.action ||
                  filters.entity ||
                  filters.startDate ||
                  filters.endDate
                    ? "No audit logs match your filters"
                    : "No audit logs found"}
                </p>
              </div>
            ) : (
              <div className="audit-logs-table-container">
                <div className="table-wrapper">
                  <table className="audit-logs-table">
                    <thead>
                      <tr>
                        <th style={{ width: "50px" }}></th>
                        <th>Timestamp</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Entity</th>
                        <th>Record ID</th>
                        <th>Application</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => (
                        <React.Fragment key={log.id}>
                          <tr
                            className={expandedRow === log.id ? "expanded" : ""}
                            onClick={() => toggleRow(log.id)}
                          >
                            <td className="col-expand">
                              <span className="expand-icon">
                                {expandedRow === log.id ? "▼" : "▶"}
                              </span>
                            </td>
                            <td className="col-timestamp">
                              {formatDateTime(log.createdAt)}
                            </td>
                            <td className="col-user">
                              <div>{log.fullName}</div>
                              <div
                                style={{ fontSize: "0.75rem", opacity: 0.7 }}
                              >
                                @{log.userName}
                              </div>
                            </td>
                            <td className="col-action">
                              <span
                                className={`action-badge ${getActionBadgeClass(
                                  log.userAction,
                                )}`}
                              >
                                {log.userAction}
                              </span>
                            </td>
                            <td className="col-entity">{log.entityName}</td>
                            <td>{log.recordId}</td>
                            <td>{log.applicationName}</td>
                          </tr>
                          {expandedRow === log.id && (
                            <tr className="expanded-row">
                              <td colSpan={7}>
                                <div className="details-panel">
                                  <h4 className="details-panel__title">
                                    <span>📝</span>
                                    Change Details
                                  </h4>
                                  {log.details && log.details.length > 0 ? (
                                    <table className="details-table">
                                      <thead>
                                        <tr>
                                          <th>Field Name</th>
                                          <th>Old Value</th>
                                          <th>New Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {log.details.map((detail) => (
                                          <tr key={detail.id}>
                                            <td>{detail.fieldName}</td>
                                            <td className="value-old">
                                              {detail.oldValue || "(empty)"}
                                            </td>
                                            <td className="value-new">
                                              {detail.newValue || "(empty)"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <div className="no-details">
                                      No detailed changes recorded for this
                                      action
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AuditLogs;
